import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { getConversions, type ConversionEvent, type ConversionsReport } from '../lib/adminClient'
import { LoadError } from '../components/LoadError'
import { DetailGrid, ExpandableRow, useOpenRow } from '../components/RowDetail'
import { AccountLink, Drillable, DrillValue } from '../components/Drill'
import { dateTime, num } from '../lib/format'

// The people who became customers, or asked to, and the copies of the app that
// went out (server: conversions.ts). Owner, 2026-10-29: "a reliable way of
// tracking new downloads, signups and form conversions with client data, not
// general system information".
//
// Every line comes from its primary record: the account, the form as sent, the
// checkout. A download carries no identity, so downloads are counts per day,
// and the page never pairs one with a person. Rows recovered from before the
// console recorded them are marked.

const RANGES = [7, 30, 90, 365]
const TYPE_LABEL: Record<ConversionEvent['type'], string> = { signup: 'Sign-up', form: 'Form', checkout: 'Checkout' }
const TYPE_TONE: Record<ConversionEvent['type'], string> = {
  signup: 'bg-violet-500/15 text-violet-200',
  form: 'bg-amber-500/15 text-amber-200',
  checkout: 'bg-emerald-500/15 text-emerald-200'
}

function csvCell(v: unknown): string {
  const s = v === null || v === undefined ? '' : String(v)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function Tile({ label, value, sub, testId }: { label: string; value: string; sub?: string; testId: string }): JSX.Element {
  return (
    <div className="glass-card rounded-xl p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">{label}</div>
      <div className="text-2xl font-semibold mt-1 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  )
}

export default function Conversions(): JSX.Element {
  const [days, setDays] = useState(30)
  const [type, setType] = useState<ConversionEvent['type'] | 'all'>('all')
  const [state, setState] = useState<{ s: 'loading' } | { s: 'ready'; r: ConversionsReport } | { s: 'error'; error: string }>({ s: 'loading' })
  const events = useOpenRow()
  const daysRow = useOpenRow()

  const load = useCallback(async (d: number) => {
    setState({ s: 'loading' })
    const res = await getConversions(d)
    setState(res.ok ? { s: 'ready', r: res.data } : { s: 'error', error: res.error })
  }, [])
  useEffect(() => { void load(days) }, [days, load])

  const rows = useMemo(
    () => (state.s === 'ready' ? state.r.events.filter((e) => type === 'all' || e.type === type) : []),
    [state, type]
  )

  function exportCsv(): void {
    if (rows.length === 0) return
    const head = ['when', 'type', 'name', 'email', 'detail', 'company', 'source', 'status', 'recovered']
    const lines = [head.join(','), ...rows.map((e) => [e.at, e.type, e.name, e.email, e.detail, e.company, e.source, e.status, e.recovered ? 'yes' : ''].map(csvCell).join(','))]
    const url = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `plexii-conversions-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Growth</div>
          <h1 className="text-2xl font-semibold mt-1">Conversions</h1>
          <div className="text-sm text-slate-400 mt-1">New downloads, sign-ups, website forms and checkouts, with the person behind each where there is one.</div>
        </div>
        <div className="flex gap-2">
          {RANGES.map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`conversions-range-${d}`} className={d === days ? 'btn-primary' : 'btn-ghost'}>
              {d === 365 ? '12 months' : `${d} days`}
            </button>
          ))}
        </div>
      </div>

      {state.s === 'loading' && <div className="glass-card rounded-xl p-6 text-sm text-slate-400">Loading conversions…</div>}
      {state.s === 'error' && <LoadError what="conversions" endpoint="GET /admin/conversions" error={state.error} testId="conversions-error" />}

      {state.s === 'ready' && (() => {
        const { r } = state
        const t = r.totals
        const fromMs = String(Date.parse(r.from))
        return (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              {/* Each tile opens the rows it counted, over the same window. */}
              <Drillable
                to={{ set: 'downloadDays', filters: { from: r.downloads[0]?.date ?? r.from.slice(0, 10) } }}
                label={`New downloads, last ${r.days} days`}
                expect={r.downloadsConfigured ? { key: 'installs', value: t.downloads.mac + t.downloads.win } : undefined}
                testId="conversions-drill-downloads"
              >
                <Tile
                  testId="conversions-downloads"
                  label="New downloads"
                  value={r.downloadsConfigured ? num(t.downloads.mac + t.downloads.win) : 'not set up'}
                  sub={r.downloadsConfigured ? `${num(t.downloads.mac)} Mac · ${num(t.downloads.win)} Windows` : 'Cloudflare analytics is not configured on the server'}
                />
              </Drillable>
              <Drillable to={{ set: 'accounts', filters: { fromMs } }} label={`Sign-ups, last ${r.days} days`} expect={{ key: 'count', value: t.signups }} testId="conversions-drill-signups">
                <Tile testId="conversions-signups" label="Sign-ups" value={num(t.signups)} sub={`${num(t.verified)} verified their email`} />
              </Drillable>
              <Drillable to={{ set: 'leads', filters: { fromMs } }} label={`Form submissions, last ${r.days} days`} expect={{ key: 'count', value: t.forms }} testId="conversions-drill-forms">
                <Tile testId="conversions-forms" label="Form submissions" value={num(t.forms)} sub="Demo, live session, early access, download link" />
              </Drillable>
              <Drillable to={{ set: 'funnel', filters: { event: 'checkout_started', fromMs } }} label={`Checkouts, last ${r.days} days`} expect={{ key: 'count', value: t.checkouts }} testId="conversions-drill-checkouts">
                <Tile testId="conversions-checkouts" label="Checkouts" value={num(t.checkouts)} sub={`${num(t.paid)} paid`} />
              </Drillable>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 mb-3">
              {(['all', 'signup', 'form', 'checkout'] as const).map((k) => (
                <button key={k} onClick={() => setType(k)} data-testid={`conversions-type-${k}`}
                  className={`px-3 py-1.5 rounded-md text-sm border ${type === k ? 'border-accent/60 bg-accent/10 text-slate-100' : 'border-white/10 text-slate-400 hover:text-slate-200'}`}>
                  {k === 'all' ? 'Everything' : `${TYPE_LABEL[k]}s`}
                </button>
              ))}
              <button onClick={exportCsv} disabled={rows.length === 0} data-testid="conversions-export" className="btn-ghost ml-auto disabled:opacity-40">Export CSV</button>
            </div>

            <div className="glass-card rounded-xl overflow-hidden mb-6">
              {rows.length === 0 ? (
                <div className="p-6 text-sm text-slate-400" data-testid="conversions-empty">Nothing in the last {r.days} days.</div>
              ) : (
                <table className="admin-table" data-testid="conversions-table">
                  <thead>
                    <tr><th>When</th><th>What</th><th>Who</th><th>Detail</th><th>Came from</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {rows.map((e, i) => (
                      <ExpandableRow
                        key={`${e.type}-${e.at}-${i}`}
                        open={events.isOpen(`${e.type}-${e.at}-${i}`)}
                        onToggle={() => events.toggle(`${e.type}-${e.at}-${i}`)}
                        colSpan={6}
                        testId="conversion-row"
                        detail={() => <EventDetail e={e} />}
                      >
                        <td className="whitespace-nowrap tabular-nums text-slate-300">{dateTime(e.at)}</td>
                        <td><span className={`px-1.5 py-0.5 rounded text-[11px] ${TYPE_TONE[e.type]}`}>{TYPE_LABEL[e.type]}</span></td>
                        <td>
                          <div className="text-slate-100">
                            <AccountLink id={e.accountId}>{e.name || <span className="text-slate-500">no name given</span>}</AccountLink>
                          </div>
                          <div className="text-xs text-slate-400">{e.email}</div>
                        </td>
                        <td>
                          <div className="text-slate-200">{e.detail || '—'}</div>
                          {e.company && <div className="text-xs text-slate-400">{e.company}</div>}
                        </td>
                        <td className="text-slate-300">{e.source || <span className="text-slate-500">unknown</span>}</td>
                        <td>
                          <div className="text-slate-200">{e.status}</div>
                          {e.recovered && <div className="text-[10px] text-slate-500" title="Recovered from its record, from before the console stored it">recovered</div>}
                        </td>
                      </ExpandableRow>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {r.downloadsConfigured && (
              <div className="glass-card rounded-xl p-4" data-testid="conversions-downloads-by-day">
                <div className="text-sm text-slate-200 mb-2">New downloads by day</div>
                <div className="text-xs text-slate-400 mb-3">
                  Installer downloads from dl.plexiidesk.com, our own machines left out. App updates are not counted. A download has no
                  name attached, so these are counts, not people. Cloudflare samples this traffic about 1 in {num(r.downloadSampleInterval)},
                  so each figure is accurate to roughly ±{num(r.downloadSampleInterval)}.
                </div>
                <table className="admin-table">
                  <thead><tr><th>Day</th><th className="text-right">Mac</th><th className="text-right">Windows</th></tr></thead>
                  <tbody>
                    {r.downloads.filter((d) => d.mac || d.win).reverse().map((d) => (
                      <ExpandableRow
                        key={d.date}
                        open={daysRow.isOpen(d.date)}
                        onToggle={() => daysRow.toggle(d.date)}
                        colSpan={3}
                        testId={`download-day-${d.date}`}
                        detail={() => {
                          const all = t.downloads.mac + t.downloads.win
                          return (
                            <DetailGrid
                              facts={[
                                ['Day', `${d.date} (UTC)`],
                                ['Mac', num(d.mac)],
                                ['Windows', num(d.win)],
                                ['Both', num((d.mac ?? 0) + (d.win ?? 0))],
                                ['Share of the range', all ? `${Math.round((((d.mac ?? 0) + (d.win ?? 0)) / all) * 100)}% of ${num(all)}` : null],
                                ['Accuracy', `About ±${num(r.downloadSampleInterval)}: Cloudflare samples 1 in ${num(r.downloadSampleInterval)} requests`]
                              ]}
                            />
                          )
                        }}
                      >
                        <td className="tabular-nums">{d.date}</td>
                        {(['mac', 'win'] as const).map((os) => (
                          <td key={os} className="text-right tabular-nums">
                            {d[os] === null ? (
                              num(d[os])
                            ) : (
                              <DrillValue
                                to={{ set: 'downloadRequests', filters: { date: d.date, os, self: '0' } }}
                                label={`${os === 'mac' ? 'Mac' : 'Windows'} downloads on ${d.date}`}
                                expect={{ key: 'requests', value: d[os] ?? 0 }}
                              >
                                {num(d[os])}
                              </DrillValue>
                            )}
                          </td>
                        ))}
                      </ExpandableRow>
                    ))}
                  </tbody>
                </table>
                {(() => {
                  // "No downloads" only for days Cloudflare was asked about; a day
                  // never fetched is unknown, not zero.
                  const unknown = r.downloads.filter((d) => d.mac === null && d.win === null).length
                  const known = r.downloads.length - unknown
                  return (
                    <>
                      {known > 0 && r.downloads.every((d) => !d.mac && !d.win) && (
                        <div className="text-sm text-slate-400 mt-2">No new downloads on the {num(known)} days fetched.</div>
                      )}
                      {unknown > 0 && (
                        <div className="text-xs text-amber-200 mt-2" data-testid="conversions-downloads-unfetched">
                          {unknown === r.downloads.length
                            ? 'Nothing has been fetched from Cloudflare for this range yet, so there are no download figures, not zero downloads.'
                            : `${num(unknown)} of ${num(r.downloads.length)} days were never fetched from Cloudflare, so they are not counted.`}
                        </div>
                      )}
                    </>
                  )
                })()}
              </div>
            )}
          </>
        )
      })()}
    </div>
  )
}

const TYPE_MEANING: Record<ConversionEvent['type'], string> = {
  signup: 'Created an account',
  form: 'Sent a form on the website',
  checkout: 'Started paying for a plan'
}

// Everything the server knows about one conversion.
function EventDetail({ e }: { e: ConversionEvent }): JSX.Element {
  return (
    <DetailGrid
      facts={[
        ['What', `${TYPE_LABEL[e.type]}: ${TYPE_MEANING[e.type]}`],
        ['When', dateTime(e.at)],
        ['Name', e.name],
        ['Email', e.email],
        ['Company', e.company],
        ['Came from', e.source ?? 'Unknown'],
        ['Status', e.status],
        ['Account id', e.accountId ? <AccountLink id={e.accountId}><span className="font-mono">{e.accountId}</span></AccountLink> : 'No account'],
        ['Lead id', e.leadId ? <DrillValue to={{ set: 'leads', filters: { id: e.leadId } }} label={`Form from ${e.email}`}><span className="font-mono">{e.leadId}</span></DrillValue> : null],
        ['Recorded', e.recovered ? 'Recovered from its own record, from before the console stored conversions' : 'As it happened']
      ]}
      wide={[['Detail', e.detail]]}
    >
      <Link className="btn-ghost text-xs" to={`/emails?tab=log&q=${encodeURIComponent(e.email)}`} data-testid="conversion-emails">
        Emails sent to them
      </Link>
    </DetailGrid>
  )
}
